/**
 * Accept and reject projections in pure normal form, and the measured normalization list
 * (architecture 4.3a and 4.4; WP1, ported from the lab's measured projection and canonical form).
 *
 * Accepting removes what a Deletion marks and keeps what an Insertion marks; rejecting the
 * reverse. As the editor itself does it: an inline, a row, a table whose every row, a paragraph
 * whose mark and every run, and a content control whose every run carry the change disappear with
 * it; everything else keeps its place with `pending` dropped. WP1 measured this projection equal
 * to the editor's own acceptAll except for run re-splitting (N6), empty-control removal (handled
 * here) and bookmark ends (an invariant, not a normalization).
 *
 * The proof admits exactly these differences:
 *
 *   N1  a computed field's cached result belongs to the editor: the editor rewrites a PAGE,
 *       NUMPAGES or SECTIONPAGES result on open, wherever the field sits, and rebuilds a table of
 *       contents after every change (its whole result: entries, HYPERLINK and PAGEREF results,
 *       their formats, the hidden `_Toc` bookmarks it anchors entries to, and the `TOC n` styles
 *       its levels need)
 *   N2  created runs and marks get an empty character format (absorbed: the normal form never
 *       references an empty format, and this drops any reference to one)
 *   N3  `insertText` writes `bidi: false` into the character format
 *   N4  the root `trackChanges` mirrors the editor's flag
 *   N5  `styleName: Normal` is dropped from empty paragraphs at open
 *   N6  runs are re-split at layout boundaries while pending
 *   N8  an over-wide table's cellWidth and grid are scaled to the page (absorbed: both live in
 *       the residue, which the proof compares without geometry)
 *   N14 a run property written explicitly as the value it inherits is the same as absent
 *
 * and, only where an undo is compared with the document it should restore:
 *
 *   N7  undoing a table operation adds row default keys (gridBefore, gridAfter and their widths)
 */
import type { Normalization } from '../../pack';
import type { FormatEntry, NfNode, NormalForm } from '../../tree';
import { canonicalJson, hash64 } from '../../tree';
import {
  FEATURE_KEYS,
  HEADER_FOOTER,
  KIND,
  NATIVE_SETTERS
} from './adapter/keys';
import { revisionsIn } from './adapter/revisions';
import { tocField } from './toc';
import { docxTree } from './tree';
import { arr } from './util';

type Obj = Record<string, unknown>;
const isObject = (v: unknown): v is Obj =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

// ------------------------------------------------------------------ projections

/** The change set being resolved; undefined resolves every pending change. */
let scope: string | undefined;
const kindsOf = (pending: unknown): string[] =>
  revisionsIn(pending)
    .filter((r) => scope === undefined || r.group === scope)
    .map((r) => r.kind);
/**
 * Whether a pending view carries a change of `kind`: applying it removes the anchor. A run inserted
 * by one change and deleted by another is gone whichever way the document is resolved (measured:
 * the editor's accept-all and reject-all both remove it).
 */
const allOf = (pending: unknown, kind: string) =>
  kindsOf(pending).includes(kind);

/** A pending view less the revisions of change set `group` (its mark's too); null when empty. */
export function withoutGroup(pending: unknown, group: string): Obj | null {
  if (!isObject(pending)) return null;
  const view = (revisions: ReturnType<typeof revisionsIn>): Obj | null => {
    const kept = revisions.filter((r) => r.group !== group);
    if (!kept.length) return null;
    return kept.length > 1 ? { ...kept[0], revisions: kept } : { ...kept[0] };
  };
  const own = view(revisionsIn(pending));
  const mark = view(revisionsIn(pending.mark));
  if (!own && !mark) return null;
  return { ...(own ?? {}), ...(mark ? { mark } : {}) };
}

const runsUnder = (node: NfNode): NfNode[] => {
  const out: NfNode[] = [];
  const rec = (n: NfNode) => {
    for (const key of docxTree.childLists(n)) {
      if (n.kind === KIND.table) continue;
      for (const c of arr<NfNode>(listOf(n, key))) {
        if (c.kind === KIND.run) out.push(c);
        rec(c);
      }
    }
  };
  rec(node);
  return out;
};

function listOf(node: Obj, key: string): unknown[] | undefined {
  let cur: unknown = node;
  for (const part of key.split('/'))
    cur = isObject(cur) ? cur[part] : undefined;
  return Array.isArray(cur) ? cur : undefined;
}

/** Whether a change of `kind`, once applied, removes this node. */
function removedBy(node: NfNode, kind: string): boolean {
  if (allOf(node.pending, kind)) return true;
  if (node.kind === KIND.table) {
    const rows = arr<NfNode>(node.rows);
    return rows.length > 0 && rows.every((r) => allOf(r.pending, kind));
  }
  if (node.kind === KIND.paragraph) {
    const mark = isObject(node.pending) ? node.pending.mark : undefined;
    // an empty run with no change of its own is no run (N13): the editor adds one inside a nested
    // field's result on open (a deleted contents' first entry), and it does not keep the paragraph
    const runs = runsUnder(node).filter(
      (r) => r.text !== '' || r.pending !== undefined
    );
    return allOf(mark, kind) && runs.every((r) => allOf(r.pending, kind));
  }
  if (node.kind === KIND.control) {
    const runs = runsUnder(node);
    if (runs.length && runs.every((r) => allOf(r.pending, kind))) return true;
    const tables = arr<NfNode>(node.blocks).filter(
      (b) => b.kind === KIND.table
    );
    return tables.length > 0 && tables.every((t) => removedBy(t, kind));
  }
  // a cell or a section whose every block goes goes with them (the editor's own reject removes an
  // inserted column's cells and an inserted section; WP1, measured in the headless lane)
  if (node.kind === KIND.cell || node.kind === KIND.section) {
    const blocks = arr<NfNode>(node.blocks);
    return blocks.length > 0 && blocks.every((b) => removedBy(b, kind));
  }
  return false;
}

function project(nf: NormalForm, drop: string, group?: string): NormalForm {
  const out = clone(nf);
  const rec = (n: NfNode) => {
    if (group === undefined) delete n.pending;
    else {
      const rest = withoutGroup(n.pending, group);
      if (rest) n.pending = rest;
      else delete n.pending;
    }
    for (const key of docxTree.childLists(n)) {
      const list = listOf(n, key) as NfNode[] | undefined;
      if (!list) continue;
      for (let i = list.length - 1; i >= 0; i -= 1) {
        let removed: boolean;
        scope = group;
        try {
          removed = removedBy(list[i], drop);
        } finally {
          scope = undefined;
        }
        if (removed) list.splice(i, 1);
        else rec(list[i]);
      }
    }
  };
  rec(out.root);
  return out;
}

/** All pending changes accepted. */
export const accept = (nf: NormalForm): NormalForm => project(nf, 'Deletion');
/** All pending changes rejected. */
export const reject = (nf: NormalForm): NormalForm => project(nf, 'Insertion');
/**
 * The move a copy belongs to: the marker every revision of change set `group` under it carries,
 * when they all carry the same one; undefined otherwise (a plain deletion or insertion).
 */
function moveOf(node: NfNode, group: string): string | undefined {
  const seen = new Set<string | undefined>();
  const rec = (n: NfNode) => {
    if (isObject(n.pending))
      for (const r of [
        ...revisionsIn(n.pending),
        ...revisionsIn(n.pending.mark)
      ])
        if (r.group === group) seen.add(r.move);
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key))) rec(c);
  };
  rec(node);
  const [only] = [...seen];
  return seen.size === 1 ? only : undefined;
}

const hasBookmarks = (n: unknown): boolean => {
  if (Array.isArray(n)) return n.some(hasBookmarks);
  if (!isObject(n)) return false;
  if (n.kind === KIND.bookmark) return true;
  return Object.values(n).some(hasBookmarks);
};

/**
 * The moves of change set `group`: each a copy the change set deletes where the node was and a
 * copy it inserts where the node goes, paired by the move marker the change set recorded on both
 * (it travels in the group tag, so it survives a reload). A deletion and an insertion of equal
 * content are not a move, and a marker that does not name exactly one copy of each is no pair.
 */
function movesOf(
  nf: NormalForm,
  group: string
): Array<{ removed: NfNode; placed: NfNode }> {
  const gone = new Map<string, NfNode[]>();
  const placed = new Map<string, NfNode[]>();
  const rec = (n: NfNode) => {
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key))) {
        scope = group;
        let inserted: boolean;
        let deleted: boolean;
        try {
          inserted = removedBy(c, 'Insertion');
          deleted = !inserted && removedBy(c, 'Deletion');
        } finally {
          scope = undefined;
        }
        const move = inserted || deleted ? moveOf(c, group) : undefined;
        if (move) {
          const into = inserted ? placed : gone;
          into.set(move, [...(into.get(move) ?? []), c]);
        } else rec(c);
      }
  };
  rec(nf.root);
  const out: Array<{ removed: NfNode; placed: NfNode }> = [];
  for (const [move, removed] of gone) {
    const to = placed.get(move) ?? [];
    if (removed.length === 1 && to.length === 1)
      out.push({ removed: removed[0], placed: to[0] });
  }
  return out;
}

/** An inline's length in its paragraph's own text: a run's text, a control's runs, else none. */
function textLength(n: NfNode): number {
  if (n.kind === KIND.run) return String(n.text ?? '').length;
  return arr<NfNode>(n.inlines).reduce((k, c) => k + textLength(c), 0);
}

/**
 * Put bookmarks into an inline list at own-text positions: each goes before the first inline that
 * starts at or after its position, a run it falls inside being split there; a bookmark at the end
 * of the text it came from goes at the end of this one, whatever text the person changed.
 */
function placeBookmarks(
  list: NfNode[],
  marks: Array<{ at: number; end: boolean; node: NfNode }>
): NfNode[] {
  const total = list.reduce((k, x) => k + textLength(x), 0);
  const pending = marks.map((m) => ({
    ...m,
    at: m.end ? total : Math.min(m.at, total)
  }));
  const out: NfNode[] = [];
  let at = 0;
  const flush = (upTo: number) => {
    for (let m = pending[0]; m && m.at <= upTo; m = pending[0]) {
      out.push(m.node);
      pending.shift();
    }
  };
  for (const x of list) {
    flush(at);
    const len = textLength(x);
    // a bookmark inside this run: split the run there, its halves keeping its fields
    while (
      x.kind === KIND.run &&
      pending.length &&
      pending[0].at > at &&
      pending[0].at < at + len
    ) {
      const cut = pending[0].at - at;
      const text = String(x.text ?? '');
      out.push({ ...clone(x), id: x.id, text: text.slice(0, cut) });
      x.text = text.slice(cut);
      delete (x as { id?: string }).id;
      at += cut;
      flush(at);
    }
    out.push(x);
    at += textLength(x);
  }
  flush(Number.POSITIVE_INFINITY);
  return out;
}

/**
 * Carry the bookmarks of a move's inserted copy back onto the copy rejecting restores. A bookmark's
 * place in an inline list is a position in the paragraph's own text, never a child index, because
 * the person may have split or joined the moved copy's runs (formatting part of it) before the
 * reject: each bookmark goes back at its text position (its end position, if it closed the text).
 */
function carryBookmarks(to: NfNode, from: NfNode): void {
  const walk = (a: NfNode, b: NfNode) => {
    for (const key of docxTree.childLists(b)) {
      const listA = listOf(a, key) as NfNode[] | undefined;
      const listB = listOf(b, key) as NfNode[] | undefined;
      if (!listA || !listB) continue;
      if (key === 'inlines' || key.endsWith('/inlines')) {
        const totalB = listB.reduce((k, x) => k + textLength(x), 0);
        const marks: Array<{ at: number; end: boolean; node: NfNode }> = [];
        let at = 0;
        for (const x of listB) {
          if (x.kind === KIND.bookmark)
            marks.push({ at, end: at === totalB && at > 0, node: clone(x) });
          else at += textLength(x);
        }
        const own = listA.filter((x) => x.kind !== KIND.bookmark);
        const ownB = listB.filter((x) => x.kind !== KIND.bookmark);
        // nested inline lists (a control's) pair up when the two copies hold the same controls
        if (own.length === ownB.length) own.forEach((x, k) => walk(x, ownB[k]));
        listA.splice(0, listA.length, ...placeBookmarks(own, marks));
        continue;
      }
      if (listA.length !== listB.length) continue;
      listA.forEach((x, i) => walk(x, listB[i]));
    }
  };
  walk(to, from);
}

/**
 * One change set's card resolved, every other pending change left as it is: what the engine
 * applies when it owns a card's accept or reject (a structural card, a feature replacement the
 * editor's own resolution would leave as an empty control, or a bookmarked move, whose reject
 * carries the bookmarks back to where the node was).
 */
export const resolveGroup = (
  nf: NormalForm,
  group: string,
  acceptIt: boolean
): NormalForm => {
  if (acceptIt) return project(nf, 'Deletion', group);
  const moves = movesOf(nf, group);
  if (!moves.length) return project(nf, 'Insertion', group);
  const copy = clone(nf);
  const index = new Map<NfNode, NfNode>();
  // the same nodes in the copy, by position
  const pair = (a: NfNode, b: NfNode) => {
    index.set(a, b);
    for (const key of docxTree.childLists(a)) {
      const la = arr<NfNode>(listOf(a, key));
      const lb = arr<NfNode>(listOf(b, key));
      la.forEach((x, i) => lb[i] && pair(x, lb[i]));
    }
  };
  pair(nf.root, copy.root);
  for (const { removed, placed } of moves)
    if (hasBookmarks(placed))
      carryBookmarks(index.get(removed) as NfNode, placed);
  return project(copy, 'Insertion', group);
};

// ------------------------------------------------------------------ normalizations

/** Apply `edit` to every node; format entries are rewritten copy-on-write. */
/**
 * Apply `edit` to every node of a copy; `edit` returns true when it changed something. When
 * nothing changed the input itself is returned, so the proof can skip re-reading it.
 */
function eachNode(
  nf: NormalForm,
  edit: (
    node: NfNode,
    inStory: boolean,
    out: NormalForm,
    paragraph: NfNode | undefined
  ) => boolean | void
): NormalForm {
  const out = clone(nf);
  let changed = false;
  const rec = (n: NfNode, inStory: boolean, paragraph?: NfNode) => {
    const enclosing = n.kind === KIND.paragraph ? n : paragraph;
    if (edit(n, inStory, out, enclosing)) changed = true;
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key)))
        rec(c, inStory || key.startsWith(`${HEADER_FOOTER}/`), enclosing);
  };
  rec(out.root, false);
  return changed ? out : nf;
}

const REF_KEYS = ['style', 'markStyle', 'listStyle'];

/** Rewrite a referenced format entry under a fresh key; the original stays for other referrers. */
function rewriteRef(
  out: NormalForm,
  n: NfNode,
  key: string,
  change: (e: FormatEntry) => FormatEntry
): boolean {
  const ref = n[key];
  if (typeof ref !== 'string' || !out.formats[ref]) return false;
  const next = change({ ...out.formats[ref] });
  if (!Object.keys(next).length) {
    delete n[key];
    return true;
  }
  const id = `${ref}~${hash64(canonicalJson(next))}`;
  out.formats[id] = next;
  n[key] = id;
  return true;
}

const textOf = (n: NfNode): string =>
  runsUnder(n)
    .map((r) => String(r.text ?? ''))
    .join('');

/**
 * The field codes whose cached result the editor recomputes when it opens a document, measured in
 * the headless lane: PAGE, NUMPAGES and SECTIONPAGES are rewritten (whatever their case and
 * switches); SECTION, DATE, TIME, AUTHOR, REF, MERGEFIELD, HYPERLINK, FILENAME and TOC keep what
 * was written.
 */
const COMPUTED_FIELDS = new Set(['PAGE', 'NUMPAGES', 'SECTIONPAGES']);

/** A paragraph's inline nodes in reading order, through inline controls. */
function inlineOrder(paragraph: NfNode): NfNode[] {
  const out: NfNode[] = [];
  const rec = (inlines: unknown) => {
    for (const i of arr<NfNode>(inlines)) {
      out.push(i);
      if (Array.isArray(i.inlines)) rec(i.inlines);
    }
  };
  rec(paragraph.inlines);
  return out;
}

/**
 * A table of contents field as one placeholder paragraph, and no `_Toc` bookmark anywhere: the editor
 * rebuilds the field's result from the headings (`seams/toc.ts`), entries, page numbers and the
 * bookmarks the entries point at, and none of it is the write's to prove.
 */
function withoutToc(nf: NormalForm): NormalForm {
  const toc = tocField(nf);
  if (!toc) return nf;
  const out = clone(nf);
  const first = toc.paragraphs[0].id;
  let done = false;
  const rec = (n: NfNode) => {
    for (const key of docxTree.childLists(n)) {
      const list = listOf(n, key);
      if (!Array.isArray(list)) continue;
      const at = list.findIndex((c) => (c as NfNode)?.id === first);
      if (
        !done &&
        at >= 0 &&
        n.id === toc.container.id &&
        key === toc.listKey
      ) {
        // only the field is the editor's: a title before its begin and text after its end stay,
        // in a paragraph of their own (the editor puts the rebuilt field in a paragraph of its own)
        const head = list[at] as NfNode;
        const tail = list[at + toc.paragraphs.length - 1] as NfNode;
        const prefix = arr<NfNode>(head.inlines).slice(0, toc.beginAt);
        const suffix = arr<NfNode>(tail.inlines).slice(toc.endAt + 1);
        list.splice(
          at,
          toc.paragraphs.length,
          ...(prefix.length ? [{ ...head, inlines: prefix }] : []),
          { id: first, kind: KIND.paragraph, toc: 'refreshed by the editor' },
          ...(suffix.length ? [{ ...tail, inlines: suffix }] : [])
        );
        done = true;
      }
      for (let k = list.length - 1; k >= 0; k -= 1) {
        const c = list[k] as NfNode;
        if (c?.kind === KIND.bookmark && /^_toc/i.test(String(c.name ?? '')))
          list.splice(k, 1);
        else if (c && typeof c === 'object') rec(c);
      }
    }
  };
  rec(out.root);
  // the TOC n paragraph styles are the contents' own: the rebuild adds the ones its levels need
  if (Array.isArray(out.root.styles))
    out.root.styles = (out.root.styles as Array<{ name?: unknown }>).filter(
      (st) => !/^TOC \d$/.test(String(st?.name ?? ''))
    );
  return out;
}

export const N1: Normalization = {
  name: 'N1',
  // a computed field's cached result belongs to the editor, wherever the field sits
  apply: (input) =>
    eachNode(withoutToc(input), (n) => {
      if (n.kind !== KIND.paragraph) return false;
      let changed = false;
      const open: Array<{ code: string; result: boolean }> = [];
      for (const i of inlineOrder(n)) {
        const top = open[open.length - 1];
        if (i.kind === KIND.field && i.fieldType === 0)
          open.push({ code: '', result: false });
        else if (i.kind === KIND.field && i.fieldType === 2) {
          if (top) top.result = true;
        } else if (i.kind === KIND.field && i.fieldType === 1) open.pop();
        else if (i.kind === KIND.run && top && typeof i.text === 'string') {
          if (!top.result) top.code += i.text;
          else if (
            COMPUTED_FIELDS.has(
              top.code.trim().split(/\s+/)[0].toUpperCase()
            ) &&
            i.text !== '#'
          ) {
            i.text = '#';
            changed = true;
          }
        }
      }
      return changed;
    })
};

export const N2: Normalization = {
  name: 'N2',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      let changed = false;
      for (const key of REF_KEYS)
        if (
          typeof n[key] === 'string' &&
          out.formats[n[key] as string] &&
          !Object.keys(out.formats[n[key] as string]).length
        ) {
          delete n[key];
          changed = true;
        }
      return changed;
    })
};

export const N3: Normalization = {
  name: 'N3',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      const key =
        n.kind === KIND.paragraph
          ? 'markStyle'
          : n.kind === KIND.run
          ? 'style'
          : null;
      if (!key) return false;
      const ref = n[key];
      if (typeof ref === 'string' && out.formats[ref]?.bidi === false)
        return rewriteRef(out, n, key, (e) => {
          delete e.bidi;
          return e;
        });
      return false;
    })
};

export const N4: Normalization = {
  name: 'N4',
  apply: (nf) => {
    if (!('trackChanges' in nf.root)) return nf;
    const out = clone(nf);
    delete out.root.trackChanges;
    return out;
  }
};

export const N5: Normalization = {
  name: 'N5',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      if (n.kind !== KIND.paragraph || textOf(n).length) return false;
      const ref = n.style;
      if (typeof ref === 'string' && out.formats[ref]?.styleName === 'Normal')
        return rewriteRef(out, n, 'style', (e) => {
          delete e.styleName;
          return e;
        });
      return false;
    })
};

export const N6: Normalization = {
  name: 'N6',
  apply: (nf) =>
    eachNode(nf, (n) => {
      if (!Array.isArray(n.inlines)) return false;
      const before = (n.inlines as NfNode[]).length;
      const merged: NfNode[] = [];
      for (const inline of n.inlines as NfNode[]) {
        const prev = merged[merged.length - 1];
        if (prev && prev.kind === KIND.run && inline.kind === KIND.run) {
          const { text: a, id: ia, ...pa } = prev;
          const { text: b, id: ib, ...pb } = inline;
          if (
            ia !== undefined &&
            ib !== undefined &&
            JSON.stringify(pa) === JSON.stringify(pb)
          ) {
            prev.text = `${String(a)}${String(b)}`;
            continue;
          }
        }
        merged.push(inline);
      }
      n.inlines = merged;
      return merged.length !== before;
    })
};

/** An object with nothing in it at any depth (`borders` with six empty sides, say). */
const emptyDeep = (v: unknown): boolean =>
  isObject(v) && Object.values(v).every(emptyDeep);

/**
 * N11: a paragraph whose format is only the Normal style (or nothing), with empty borders and an
 * empty list format beside it, is the same as one with no format: the editor gives a paragraph written
 * without one exactly that (measured in the headless lane), and Normal is the default style.
 */
export const N11: Normalization = {
  name: 'N11',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      if (n.kind !== KIND.paragraph || typeof n.style !== 'string')
        return false;
      const entry = out.formats[n.style];
      if (!entry) return false;
      // nothing but empty members, or only the Normal style beside them (an empty paragraph has
      // already lost its Normal to N5)
      const rest = Object.entries(entry).filter(([, v]) => !emptyDeep(v));
      if (
        rest.length > 1 ||
        (rest.length === 1 &&
          (rest[0][0] !== 'styleName' || rest[0][1] !== 'Normal'))
      )
        return false;
      delete n.style;
      return true;
    })
};

/** What the editor fills into a table, row or cell format written without it (measured in the lane). */
const TABLE_PART_DEFAULTS: Readonly<
  Record<string, Readonly<Record<string, unknown>>>
> = {
  [KIND.table]: {
    topMargin: 0,
    bottomMargin: 0,
    leftMargin: 5.4,
    rightMargin: 5.4
  },
  [KIND.row]: { gridBefore: 0, gridAfter: 0 },
  [KIND.cell]: { columnSpan: 1, rowSpan: 1 }
};

/**
 * N12: a table, row or cell format the editor fills in is the same as one written without the
 * fill: borders with every side empty, an empty shading, a shading's `empty` foreground and
 * `TextureNone` texture, a cell spanning one column and one row, no grid before or after a row,
 * and a table's default margins (measured: a table written with only widths and a fill reads back
 * with exactly these). A value the write set to anything else still counts.
 */
export const N12: Normalization = {
  name: 'N12',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      const defaults = TABLE_PART_DEFAULTS[n.kind];
      if (!defaults || typeof n.style !== 'string') return false;
      const entry = out.formats[n.style];
      if (!entry) return false;
      const next: FormatEntry = {};
      for (const [k, v] of Object.entries(entry)) {
        if (k in defaults && JSON.stringify(v) === JSON.stringify(defaults[k]))
          continue;
        if (k === 'borders' && emptyDeep(v)) continue;
        if (k === 'shading' && isObject(v)) {
          const shading = { ...v };
          if (shading.foregroundColor === 'empty')
            delete shading.foregroundColor;
          if (shading.texture === 'TextureNone') delete shading.texture;
          if (Object.keys(shading).length) next[k] = shading;
          continue;
        }
        next[k] = v;
      }
      if (canonicalJson(next) === canonicalJson(entry)) return false;
      return rewriteRef(out, n, 'style', () => next);
    })
};

/**
 * N13: a run with no text and no pending change is the same as none: the editor drops it when it
 * lays the paragraph out again (troy-1: a moved cell came back without its empty runs).
 */
export const N13: Normalization = {
  name: 'N13',
  apply: (nf) =>
    eachNode(nf, (n) => {
      if (!Array.isArray(n.inlines)) return false;
      const inlines = n.inlines as NfNode[];
      const kept = inlines.filter(
        (i) => !(i.kind === KIND.run && i.text === '' && !isObject(i.pending))
      );
      if (kept.length === inlines.length) return false;
      n.inlines = kept;
      return true;
    })
};

const ROW_UNDO_DEFAULTS = [
  'gridBefore',
  'gridAfter',
  'gridBeforeWidth',
  'gridAfterWidth',
  'gridBeforeWidthType',
  'gridAfterWidthType'
];
export const N7: Normalization = {
  name: 'N7',
  apply: (nf) =>
    eachNode(nf, (n, _s, out) => {
      if (n.kind !== KIND.row) return false;
      const ref = n.style;
      if (
        typeof ref === 'string' &&
        ROW_UNDO_DEFAULTS.some((k) => k in (out.formats[ref] ?? {}))
      )
        return rewriteRef(out, n, 'style', (e) => {
          for (const k of ROW_UNDO_DEFAULTS) delete e[k];
          return e;
        });
      return false;
    })
};

/** A format entry read whole: a hoisted reference resolved, an inline object as it is. */
const entryOf = (nf: NormalForm, ref: unknown): Obj | null =>
  typeof ref === 'string'
    ? (nf.formats[ref] as Obj | undefined) ?? null
    : isObject(ref)
    ? ref
    : null;

/**
 * N9 (undo only, measured in the headless lane): undoing a native paragraph property writes the
 * previous value back explicitly, where before it was inherited (an explicit `textAlignment: Left`
 * where the paragraph had none). A paragraph property the editor sets natively, explicit with the
 * value its named style (through `basedOn`) or the document default gives it, is the same as absent.
 */
export const N9: Normalization = {
  name: 'N9',
  apply: (nf) => {
    const styles = new Map(
      arr<Obj>(nf.root.styles).map((s) => [String(s.name), s] as const)
    );
    const documentDefault = entryOf(nf, nf.root.paragraphFormat) ?? {};
    const inherited = (styleName: unknown, key: string): unknown => {
      const seen = new Set<string>();
      let style = styles.get(String(styleName ?? 'Normal'));
      while (style && !seen.has(String(style.name))) {
        seen.add(String(style.name));
        const pf = entryOf(nf, style.paragraphFormat);
        if (pf && key in pf) return pf[key];
        style = styles.get(String(style.basedOn));
      }
      return documentDefault[key];
    };
    return eachNode(nf, (n, _s, out) => {
      if (n.kind !== KIND.paragraph || typeof n.style !== 'string')
        return false;
      const entry = out.formats[n.style];
      if (!entry) return false;
      const drop = NATIVE_SETTERS.paragraph.filter(
        (k) =>
          k in entry &&
          inherited(entry.styleName, k) !== undefined &&
          JSON.stringify(entry[k]) ===
            JSON.stringify(inherited(entry.styleName, k))
      );
      if (!drop.length) return false;
      return rewriteRef(out, n, 'style', (e) => {
        for (const k of drop) delete e[k];
        return e;
      });
    });
  }
};

/**
 * N14: a run property written explicitly with the value it inherits (its character style, then its
 * paragraph's named style through `basedOn`, then the document default) is the same as absent
 */
export const N14: Normalization = {
  name: 'N14',
  apply: (nf) => {
    const styles = new Map(
      arr<Obj>(nf.root.styles).map((s) => [String(s.name), s] as const)
    );
    const documentDefault = entryOf(nf, nf.root.characterFormat) ?? {};
    const inherited = (styleNames: unknown[], key: string): unknown => {
      for (const name of styleNames) {
        const seen = new Set<string>();
        let style = styles.get(String(name));
        while (style && !seen.has(String(style.name))) {
          seen.add(String(style.name));
          const cf = entryOf(nf, style.characterFormat);
          if (cf && key in cf) return cf[key];
          style = styles.get(String(style.basedOn));
        }
      }
      return documentDefault[key];
    };
    return eachNode(nf, (n, _s, out, paragraph) => {
      if (n.kind !== KIND.run || typeof n.style !== 'string') return false;
      const entry = out.formats[n.style];
      if (!entry) return false;
      const paragraphStyle =
        typeof paragraph?.style === 'string'
          ? out.formats[paragraph.style]?.styleName
          : undefined;
      const styleNames = [entry.styleName, paragraphStyle ?? 'Normal'].filter(
        (name) => name !== undefined
      );
      const drop = Object.keys(entry).filter(
        (k) =>
          k !== 'styleName' &&
          inherited(styleNames, k) !== undefined &&
          JSON.stringify(entry[k]) === JSON.stringify(inherited(styleNames, k))
      );
      if (!drop.length) return false;
      return rewriteRef(out, n, 'style', (e) => {
        for (const k of drop) delete e[k];
        return e;
      });
    });
  }
};

export const NORMALIZATIONS: readonly Normalization[] = [
  N1,
  N2,
  N3,
  N4,
  N5,
  N6,
  N11,
  N12,
  N13,
  N14
];
export const UNDO_NORMALIZATIONS: readonly Normalization[] = [N7, N9];

// ------------------------------------------------------------------ what rejecting restores

/**
 * Text, structure and feature attributes are tracked (a changed binding or formula is the node
 * replaced); formatting (decision D3), widths and every other property land untracked, since the
 * native format has no revision for them. So rejecting should restore the document before, with
 * each kept node's own properties, less its feature attributes, as the intended document has
 * them; its text, children and features come back from the revisions.
 */
export function expectedRejection(
  before: NormalForm,
  intended: NormalForm
): NormalForm {
  const out = clone(before);
  const later = new Map<string, NfNode>();
  const index = (n: NfNode) => {
    later.set(n.id, n);
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key))) index(c);
  };
  index(intended.root);
  Object.assign(out.formats, clone(intended.formats));
  const rec = (n: NfNode) => {
    const m = later.get(n.id);
    if (m && m.kind === n.kind) {
      const lists = new Set(
        [...docxTree.childLists(n), ...docxTree.childLists(m)].map(
          (k) => k.split('/')[0]
        )
      );
      const skip = (k: string) =>
        lists.has(k) ||
        ['id', 'kind', 'text', 'pending', ...FEATURE_KEYS].includes(k);
      for (const k of Object.keys(n)) if (!skip(k) && !(k in m)) delete n[k];
      for (const [k, v] of Object.entries(m)) if (!skip(k)) n[k] = clone(v);
    }
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key))) rec(c);
  };
  rec(out.root);
  return out;
}

// ------------------------------------------------------------------ authorship and residue

/** Whether every revision a pending view names belongs to the change set `turnId`. */
export function authoredBy(pending: unknown, turnId: string): boolean {
  if (!isObject(pending)) return false;
  const all = [...revisionsIn(pending), ...revisionsIn(pending.mark)];
  return all.length > 0 && all.every((r) => r.group === turnId);
}

/**
 * The part of a node's residue a commit must leave as it was: native data the normal form does
 * not show (image payloads, the dialect flag, control colours, unknown keys). Revision anchors are
 * rewritten by every tracked commit, geometry is derived from the cells (N8), binding tags follow
 * the binding the normal form shows, and key order may gain an anchor; none of those is compared.
 */
export function conservedResidue(entry: unknown): unknown {
  if (!isObject(entry)) return entry;
  const hidden = { ...((entry.hidden as Obj) ?? {}) };
  for (const k of [
    'revisionIds',
    'revisions',
    'grid',
    'columnCount',
    'columnIndex',
    'lastParagraphMarkCopied'
  ])
    delete hidden[k];
  const hiddenIn: Obj = {};
  for (const [k, subs] of Object.entries((entry.hiddenIn as Obj) ?? {})) {
    const kept = (subs as Array<{ k: string; v: unknown }>)
      .filter(
        (h) => !['revisionIds', 'cellWidth', 'tag', 'title'].includes(h.k)
      )
      .map((h) => ({ k: h.k, v: h.v }));
    if (kept.length) hiddenIn[k] = kept;
  }
  const keys = arr<string>(entry.keys).filter(
    (k) => k !== 'revisionIds' && k !== 'lastParagraphMarkCopied'
  );
  return { hidden, hiddenIn, keys };
}

/**
 * Whether the editor's own accept or reject cannot settle change set `group`, so the engine
 * resolves its card: a node it carries twice or a move pair whose copies differ in a feature (a
 * feature replacement keeps the old control beside the new one, and the editor keeps no revision
 * on a control's own markers, so its resolution leaves an empty control), or a revision in a
 * header or footer, whose native reject does not restore the story (architecture 7.5, probe P1b).
 */
export function ownsResolution(nf: NormalForm, group: string): boolean {
  if (
    movesOf(nf, group).some(
      ({ removed, placed }) =>
        hasBookmarks(placed) ||
        FEATURE_KEYS.some(
          (k) =>
            canonicalJson(removed[k] ?? null) !==
            canonicalJson(placed[k] ?? null)
        )
    )
  )
    return true;
  const seen = new Map<string, number>();
  let story = false;
  const ours = (n: NfNode) =>
    isObject(n.pending) &&
    [...revisionsIn(n.pending), ...revisionsIn(n.pending.mark)].some(
      (r) => r.group === group
    );
  const marked = new Set<string>();
  const rec = (n: NfNode, inStory: boolean) => {
    seen.set(n.id, (seen.get(n.id) ?? 0) + 1);
    if (ours(n)) {
      marked.add(n.id);
      if (inStory) story = true;
    }
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key)))
        rec(c, inStory || key.startsWith(`${HEADER_FOOTER}/`));
  };
  rec(nf.root, false);
  if (story) return true;
  // a twice-carried node whose copies hold this card's revisions somewhere inside
  const twice = new Set([...seen].filter(([, n]) => n > 1).map(([id]) => id));
  if (!twice.size) return false;
  let owned = false;
  const inside = (n: NfNode, under: boolean) => {
    const here = under || twice.has(n.id);
    if (here && marked.has(n.id)) owned = true;
    for (const key of docxTree.childLists(n))
      for (const c of arr<NfNode>(listOf(n, key))) inside(c, here);
  };
  inside(nf.root, false);
  return owned;
}
